import React, { useState, useEffect, useCallback } from 'react';
import { api } from '../api';
import { formatDate, formatEuro, clienteNome, statoLabel, Modal, ConfirmDialog, LoadingSpinner, Pagination } from '../components/shared';

const MESI = ['Gennaio','Febbraio','Marzo','Aprile','Maggio','Giugno','Luglio','Agosto','Settembre','Ottobre','Novembre','Dicembre'];

// ─── INPUT ANNO LIBERO ────────────────────────────────────────────────────────
function AnnoInput({ value, onChange, placeholder = 'Anno…', style = {} }) {
  return (
    <input
      type="number"
      min="1990" max="2099"
      value={value || ''}
      onChange={e => onChange(e.target.value)}
      placeholder={placeholder}
      style={{ width: 90, textAlign: 'center', fontFamily: 'monospace', ...style }}
      className="form-control"
    />
  );
}


function F24Form({ initial = {}, clientiList, onSave, onClose, loading }) {
  const [d, setD] = useState({
    id_cliente: '', tipo: 'F24', descrizione: '', data_scad: '',
    importo: '', stato: 'APERTA', note: '', ...initial,
    id_cliente: initial.id_cliente || '',
    importo: initial.importo || '',
  });
  const [mese, setMese] = useState(MESI[new Date().getMonth()]);
  const [anno, setAnno] = useState(new Date().getFullYear());
  const set = (k, v) => setD(p => ({ ...p, [k]: v }));

  const [modalita, setModalita] = useState('singolo');
  const isBulk = modalita !== 'singolo';
  const counts = { tutti: clientiList.length, pf: clientiList.filter(c=>c.tipo==='PF').length, pg: clientiList.filter(c=>c.tipo==='PG').length };

  return (
    <form onSubmit={(e) => { e.preventDefault(); onSave({ ...d, modalita_bulk: modalita, tipo: 'F24', descrizione: d.descrizione || `F24 ${mese} ${anno}` }); }}>
      {!initial.id && (
        <div style={{marginBottom:16}}>
          <div className="form-label" style={{marginBottom:8}}>Destinatari</div>
          <div style={{display:'grid',gridTemplateColumns:'repeat(2,1fr)',gap:6}}>
            {[['singolo','Cliente singolo',null],['tutti','Tutti i clienti',counts.tutti],['pf','Solo PF',counts.pf],['pg','Solo PG',counts.pg]].map(([k,l,n])=>(
              <button key={k} type="button" onClick={()=>setModalita(k)} style={{padding:'8px 12px',borderRadius:'var(--radius-sm)',textAlign:'left',border:`2px solid ${modalita===k?'var(--accent)':'var(--border-strong)'}`,background:modalita===k?'var(--accent-light)':'var(--surface)',cursor:'pointer'}}>
                <div style={{fontSize:12.5,fontWeight:500,color:modalita===k?'var(--accent)':'var(--text)'}}>{l}</div>
                {n!=null&&<div style={{fontSize:11,color:'var(--text-faint)'}}>{n} clienti</div>}
              </button>
            ))}
          </div>
          {isBulk&&<div style={{marginTop:8,padding:'8px 12px',background:'#FAEEDA',border:'1px solid #FAC775',borderRadius:'var(--radius-sm)',fontSize:12,color:'#854F0B'}}>⚠ Verranno creati <strong>{counts[modalita]}</strong> F24 separati</div>}
        </div>
      )}
      <div className="form-grid">
        {!isBulk && <div className="form-group form-full">
          <label className="form-label">Cliente</label>
          <select className="form-control" value={d.id_cliente} onChange={e => set('id_cliente', e.target.value)}>
            <option value="">— Seleziona cliente —</option>
            {clientiList.map(c => <option key={c.id} value={c.id}>{clienteNome(c)} ({c.codice})</option>)}
          </select>
        </div>}
        <div className="form-group">
          <label className="form-label">Mese di riferimento</label>
          <select className="form-control" value={mese} onChange={e => setMese(e.target.value)}>
            {MESI.map(m => <option key={m} value={m}>{m}</option>)}
          </select>
        </div>
        <div className="form-group">
          <label className="form-label">Anno</label>
          <AnnoInput value={anno} onChange={setAnno} placeholder="Es. 2024" />
        </div>
        <div className="form-group">
          <label className="form-label">Data Versamento *</label>
          <input className="form-control" type="date" value={d.data_scad} onChange={e => set('data_scad', e.target.value)} required />
        </div>
        <div className="form-group">
          <label className="form-label">Importo totale (€) *</label>
          <input className="form-control" type="number" step="0.01" min="0" value={d.importo} onChange={e => set('importo', e.target.value)} required />
        </div>
        <div className="form-group">
          <label className="form-label">Stato</label>
          <select className="form-control" value={d.stato} onChange={e => set('stato', e.target.value)}>
            <option value="APERTA">Aperta</option>
            <option value="PREPARATA">Preparata</option>
            <option value="INVIATA">Inviata</option>
            <option value="SOSPESA">Sospesa</option>
            <option value="NON_PREVISTA">Non prevista</option>
            <option value="ALTRO">Altro</option>
          </select>
        </div>
        <div className="form-group form-full">
          <label className="form-label">Descrizione</label>
          <input className="form-control" value={d.descrizione} onChange={e => set('descrizione', e.target.value)} placeholder={`F24 ${mese} ${anno}`} />
        </div>
        <div className="form-group form-full">
          <label className="form-label">Note / Codici tributo</label>
          <textarea className="form-control" value={d.note} onChange={e => set('note', e.target.value)} rows={3} placeholder="Es. Cod. tributo 3918 - IVA mensile…" />
        </div>
      </div>
      <div className="modal-footer" style={{ padding: '16px 0 0', borderTop: '1px solid var(--border)', marginTop: 20 }}>
        <button type="button" className="btn" onClick={onClose}>Annulla</button>
        <button type="submit" className="btn btn-primary" disabled={loading}>{loading ? 'Salvataggio…' : isBulk ? `Crea ${counts[modalita]} F24` : 'Salva F24'}</button>
      </div>
    </form>
  );
}

export default function F24({ addToast }) {
  const [data, setData]       = useState({ rows: [], total: 0 });
  const [clienti, setClienti] = useState([]);
  const [loading, setLoading] = useState(true);
  const [q, setQ]             = useState('');
  const [statoF, setStatoF]   = useState('');
  const [page, setPage]       = useState(1);
  const [showForm, setShowForm] = useState(false);
  const [editItem, setEditItem] = useState(null);
  const [delItem, setDelItem]   = useState(null);
  const [saving, setSaving]     = useState(false);

  useEffect(() => { api.getClienti({ limit: 200 }).then(d => setClienti(d.rows)); }, []);

  const load = useCallback(() => {
    setLoading(true);
    const params = { page, limit: 20, tipo: 'F24' };
    if (q) params.q = q;
    if (statoF) params.stato = statoF;
    api.getScadenze(params).then(setData).finally(() => setLoading(false));
  }, [q, statoF, page]);

  useEffect(() => { load(); }, [load]);

  const handleSave = async (formData) => {
    setSaving(true);
    try {
      if (editItem) { await api.updateScadenza(editItem.id, formData); addToast('F24 aggiornato', 'success'); setEditItem(null); }
      else { const r = await api.createScadenza(formData); addToast(r.bulk?`✅ Creati ${r.inserite} F24`:'F24 creato','success'); setShowForm(false); }
      load();
    } catch (e) { addToast(e.message, 'error'); }
    finally { setSaving(false); }
  };

  const handleDelete = async () => {
    try { await api.deleteScadenza(delItem.id); addToast('Eliminato', 'success'); setDelItem(null); load(); }
    catch (e) { addToast(e.message, 'error'); }
  };

  const handleStato = async (id, stato) => {
    try { await api.patchStato(id, stato); addToast('Stato aggiornato', 'success'); load(); }
    catch (e) { addToast(e.message, 'error'); }
  };

  const totaleVersato = data.rows.filter(r => r.stato === 'COMPLETATA').reduce((s, r) => s + (r.importo || 0), 0);
  const totaleDaVersare = data.rows.filter(r => r.stato === 'APERTA').reduce((s, r) => s + (r.importo || 0), 0);

  return (
    <div className="page-content">
      <div className="page-header">
        <div>
          <div className="page-title">F24 e Versamenti</div>
          <div className="page-subtitle">{data.total} versamenti</div>
        </div>
        <button className="btn btn-primary" onClick={() => setShowForm(true)}>+ Nuovo F24</button>
      </div>

      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(3,1fr)', gap: 14, marginBottom: 24 }}>
        {[['Da versare', formatEuro(totaleDaVersare), 'var(--c-red)'], ['Versato', formatEuro(totaleVersato), 'var(--c-green)'], ['Totale F24', data.total, 'var(--c-blue)']].map(([l, v, c]) => (
          <div key={l} className="kpi-card">
            <div className="kpi-accent" style={{ background: c }} />
            <div className="kpi-label">{l}</div>
            <div className="kpi-value" style={{ fontSize: 22 }}>{v}</div>
          </div>
        ))}
      </div>

      <div className="filters-bar">
        <div className="search-box">
          <svg width="14" height="14" viewBox="0 0 16 16" fill="none"><circle cx="7" cy="7" r="5" stroke="currentColor" strokeWidth="1.5"/><path d="M11 11l3 3" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round"/></svg>
          <input placeholder="Cerca cliente, descrizione…" value={q} onChange={e => { setQ(e.target.value); setPage(1); }} />
        </div>
        <select className="form-control" style={{ width: 'auto' }} value={statoF} onChange={e => { setStatoF(e.target.value); setPage(1); }}>
          <option value="">Tutti gli stati</option>
          <option value="APERTA">Da versare</option>
          <option value="IN_CORSO">In compilazione</option>
          <option value="COMPLETATA">Versato</option>
          <option value="ANNULLATA">Annullato</option>
        </select>
      </div>

      <div className="card">
        <div className="table-wrap">
          {loading ? <LoadingSpinner /> : (
            <table>
              <thead><tr><th>Cliente</th><th>Descrizione</th><th>Data Versamento</th><th>Importo</th><th>Stato</th><th>Azioni</th></tr></thead>
              <tbody>
                {data.rows.map(s => (
                  <tr key={s.id}>
                    <td className="fw-500">{clienteNome(s) || '—'}</td>
                    <td>{s.descrizione || '—'}</td>
                    <td style={{ fontSize: 12 }}>{formatDate(s.data_scad)}</td>
                    <td style={{ fontFamily: 'monospace', fontSize: 13, fontWeight: 500 }}>{s.importo ? formatEuro(s.importo) : '—'}</td>
                    <td>
                      <select className="form-control" style={{ padding: '3px 8px', fontSize: 11.5, width: 'auto', border: 'none', background: 'transparent', cursor: 'pointer' }}
                        value={s.stato} onChange={e => handleStato(s.id, e.target.value)}>
                        <option value="APERTA">Da versare</option>
                        <option value="IN_CORSO">In compilazione</option>
                        <option value="COMPLETATA">Versato</option>
                        <option value="ANNULLATA">Annullato</option>
                      </select>
                    </td>
                    <td><div style={{ display: 'flex', gap: 4 }}>
                      <button className="btn btn-sm" onClick={() => setEditItem(s)}>Modifica</button>
                      <button className="btn btn-sm btn-danger" onClick={() => setDelItem(s)}>×</button>
                    </div></td>
                  </tr>
                ))}
                {data.rows.length === 0 && <tr><td colSpan={6}><div className="empty-state"><div className="empty-state-icon">🏦</div><div className="empty-state-title">Nessun F24</div></div></td></tr>}
              </tbody>
            </table>
          )}
        </div>
        <Pagination page={page} total={data.total} limit={20} onPage={setPage} />
      </div>

      {(showForm || editItem) && (
        <Modal title={editItem ? 'Modifica F24' : 'Nuovo F24'} onClose={() => { setShowForm(false); setEditItem(null); }}>
          <F24Form initial={editItem || {}} clientiList={clienti} onSave={handleSave} onClose={() => { setShowForm(false); setEditItem(null); }} loading={saving} />
        </Modal>
      )}
      {delItem && <ConfirmDialog message={`Eliminare il versamento "${delItem.descrizione}"?`} onConfirm={handleDelete} onCancel={() => setDelItem(null)} />}
    </div>
  );
}
